/**
 * ContactsTab – Consolidated contacts table for client profile.
 *
 * Displays contacts from two FHIR sources per PRD:
 *   • Patient.contact[] — emergency/lightweight contacts
 *   • RelatedPerson — formal relationships (parent, guardian, spouse, etc.)
 *
 * RelatedPerson soft-delete (active: false) hides the row from Medical Record contacts
 * and Overview emergency RelatedPersons; inactive rows remain on the profile Contacts tab
 * where they can be reactivated.
 */
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMedplum } from '@medplum/react';
import type { Patient, RelatedPerson } from '@medplum/fhirtypes';
import { IconFileCertificate, IconPlus, IconTrash, IconUserCheck, IconUserOff } from '@tabler/icons-react';
import { useCallback, useState } from 'react';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import { ClientContactFormModal } from '../components/ClientContactFormModal';
import { ContactRoiModal } from '../components/ContactRoiModal';
import type { Contact } from '../data/contactTypes';
import { contactDisplayEmail, contactDisplayName, contactDisplayPhone } from '../utils/contactDisplayUtils';
import {
  contactFormToPatientContact,
  contactFormToRelatedPerson,
  deactivateRelatedPersonPatch,
  reactivateRelatedPersonPatch,
} from '../utils/clientProfileFhir';
import { resolveRoiStatus } from '../utils/contactRoiUtils';

export {
  CONTACT_RELATIONSHIP_TYPES,
  type ContactRelationshipType,
  type Contact,
  type ContactPhone,
} from '../data/contactTypes';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ContactsTabProps {
  patient: Patient | null;
  contacts: Contact[];
  loading?: boolean;
  onRefresh: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ContactsTab({
  patient,
  contacts,
  loading,
  onRefresh,
}: ContactsTabProps): JSX.Element {
  const medplum = useMedplum();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // ROI modal state
  const [roiContact, setRoiContact] = useState<Contact | null>(null);
  const roiModalOpen = roiContact !== null;

  const openRoiView = (c: Contact) => { setRoiContact(c); };
  const closeRoi = () => setRoiContact(null);

  const openAdd = () => {
    setEditingId(null);
    setModalOpen(true);
  };

  const openEdit = (c: Contact) => {
    setEditingId(c.id);
    setModalOpen(true);
  };

  const editingContact = editingId ? contacts.find((c) => c.id === editingId) ?? null : null;

  const readLatestPatient = useCallback(async (): Promise<Patient> => {
    if (!patient?.id) throw new Error('Missing patient');
    return medplum.readResource('Patient', patient.id);
  }, [medplum, patient?.id]);

  const handleSave = useCallback(
    async (payload: Omit<Contact, 'id'> & { id?: string }) => {
      if (!patient?.id) return;
      setSaving(true);
      try {
        const latestPatient = await readLatestPatient();
        const existingSource = payload.id
          ? contacts.find((c) => c.id === payload.id)?.source
          : undefined;
        const kind = payload.contactKind
          ?? (payload.source === 'related-person' ? 'related-person' : 'emergency-contact');

        if (existingSource === 'related-person' && payload.id) {
          const rpId = contacts.find((c) => c.id === payload.id)?.relatedPersonId;
          if (rpId) {
            const existingRp = await medplum.readResource('RelatedPerson', rpId);
            const updatedRp = contactFormToRelatedPerson(payload, latestPatient.id!, existingRp);
            updatedRp.id = rpId;
            await medplum.updateResource<RelatedPerson>(updatedRp);
          }
        } else if (payload.id && payload.id.startsWith('pc-')) {
          const existingContacts = [...(latestPatient.contact ?? [])];
          const idx = parseInt(payload.id.replace('pc-', ''), 10);
          if (idx >= 0 && idx < existingContacts.length) {
            existingContacts[idx] = contactFormToPatientContact(payload);
          }
          await medplum.updateResource<Patient>({
            ...latestPatient,
            contact: existingContacts.length > 0 ? existingContacts : undefined,
          });
        } else if (kind === 'related-person' || kind === 'guarantor') {
          await medplum.createResource<RelatedPerson>(
            contactFormToRelatedPerson(payload, latestPatient.id!),
          );
        } else {
          const existingContacts = [...(latestPatient.contact ?? [])];
          existingContacts.push(contactFormToPatientContact(payload));
          await medplum.updateResource<Patient>({
            ...latestPatient,
            contact: existingContacts.length > 0 ? existingContacts : undefined,
          });
        }

        notifications.show({
          color: 'green',
          title: 'Saved',
          message: payload.id ? 'Contact updated.' : 'Contact added.',
        });
        setModalOpen(false);
        await onRefresh();
      } catch (err) {
        notifications.show({
          color: 'red',
          title: 'Error',
          message: err instanceof Error ? err.message : 'Failed to save contact.',
        });
      } finally {
        setSaving(false);
      }
    },
    [patient?.id, medplum, contacts, readLatestPatient, onRefresh],
  );

  const removePatientContact = useCallback(
    async (contactRow: Contact) => {
      if (!patient?.id) return;
      setSaving(true);
      try {
        const idx = contactRow.id.startsWith('pc-') ? parseInt(contactRow.id.replace('pc-', ''), 10) : -1;
        if (idx < 0) return;

        const latestPatient = await readLatestPatient();
        const existingContacts = [...(latestPatient.contact ?? [])];
        existingContacts.splice(idx, 1);

        await medplum.updateResource<Patient>({
          ...latestPatient,
          contact: existingContacts.length > 0 ? existingContacts : undefined,
        });

        notifications.show({ color: 'green', title: 'Removed', message: 'Contact removed.' });
        await onRefresh();
      } catch (err) {
        notifications.show({
          color: 'red',
          title: 'Error',
          message: err instanceof Error ? err.message : 'Failed to remove contact.',
        });
      } finally {
        setSaving(false);
      }
    },
    [patient?.id, medplum, readLatestPatient, onRefresh],
  );

  const deactivateRelatedPerson = useCallback(
    async (contactRow: Contact) => {
      if (!contactRow.relatedPersonId) return;

      setSaving(true);
      try {
        const latestRp = await medplum.readResource('RelatedPerson', contactRow.relatedPersonId);
        await medplum.updateResource<RelatedPerson>(deactivateRelatedPersonPatch(latestRp));
        notifications.show({
          color: 'green',
          title: 'Deactivated',
          message: 'Related person has been deactivated.',
        });
        await onRefresh();
      } catch (err) {
        notifications.show({
          color: 'red',
          title: 'Error',
          message: err instanceof Error ? err.message : 'Failed to deactivate contact.',
        });
      } finally {
        setSaving(false);
      }
    },
    [medplum, onRefresh],
  );

  const reactivateRelatedPerson = useCallback(
    async (contactRow: Contact) => {
      if (!contactRow.relatedPersonId) return;

      setSaving(true);
      try {
        const latestRp = await medplum.readResource('RelatedPerson', contactRow.relatedPersonId);
        await medplum.updateResource<RelatedPerson>(reactivateRelatedPersonPatch(latestRp));
        notifications.show({
          color: 'green',
          title: 'Reactivated',
          message: 'Related person is active again on this chart.',
        });
        await onRefresh();
      } catch (err) {
        notifications.show({
          color: 'red',
          title: 'Error',
          message: err instanceof Error ? err.message : 'Failed to reactivate contact.',
        });
      } finally {
        setSaving(false);
      }
    },
    [medplum, onRefresh],
  );

  type ContactsConfirmKind = 'remove' | 'deactivate' | 'reactivate' | null;
  const [confirmKind, setConfirmKind] = useState<ContactsConfirmKind>(null);
  const [confirmContact, setConfirmContact] = useState<Contact | null>(null);

  const closeConfirm = useCallback(() => {
    setConfirmKind(null);
    setConfirmContact(null);
  }, []);

  const handleConfirmedAction = useCallback(async () => {
    if (!confirmKind || !confirmContact) return;
    try {
      if (confirmKind === 'deactivate') await deactivateRelatedPerson(confirmContact);
      else if (confirmKind === 'remove') await removePatientContact(confirmContact);
      else if (confirmKind === 'reactivate') await reactivateRelatedPerson(confirmContact);
    } finally {
      closeConfirm();
    }
  }, [confirmKind, confirmContact, deactivateRelatedPerson, removePatientContact, reactivateRelatedPerson, closeConfirm]);

  const isEdit = !!editingId;

  const columns: UnifyTableColumn<Contact>[] = [
    {
      key: 'name',
      label: 'Name',
      render: (c) => (
        <Group gap="xs">
          <Text size="sm" fw={600}>{contactDisplayName(c)}</Text>
          {c.isEmergencyContact && (
            <Badge variant="light" color="blue" size="xs">Emergency</Badge>
          )}
          {c.source === 'related-person' && (
            <Badge variant="light" color="grape" size="xs">RelatedPerson</Badge>
          )}
          {c.active === false && (
            <Badge variant="light" color="gray" size="xs">Inactive</Badge>
          )}
        </Group>
      ),
      getValue: (c) => contactDisplayName(c),
    },
    {
      key: 'relationship',
      label: 'Relationship',
      render: (c) => (
        <>
          <Text size="sm">{c.relationshipType}</Text>
          {c.role && <Text size="xs" c="gray.6">{c.role}</Text>}
        </>
      ),
      getValue: (c) => c.relationshipType,
    },
    {
      key: 'phone',
      label: 'Phone',
      render: (c) => {
        const phone = contactDisplayPhone(c);
        return phone ? (
          <Text size="sm">{phone}</Text>
        ) : (
          <Text size="sm" c="gray.6">—</Text>
        );
      },
      getValue: (c) => contactDisplayPhone(c),
    },
    {
      key: 'email',
      label: 'Email',
      render: (c) => <Text size="sm">{contactDisplayEmail(c) || '—'}</Text>,
      getValue: (c) => contactDisplayEmail(c),
    },
    {
      key: 'roi',
      label: 'ROI',
      sortable: false,
      render: (c) => {
        const status = resolveRoiStatus(c.roi);
        const colorMap: Record<typeof status, string> = {
          active: 'teal',
          expired: 'orange',
          pending: 'blue',
          none: 'gray',
        };
        const labelMap: Record<typeof status, string> = {
          active: 'Active ROI',
          expired: 'Expired ROI',
          pending: 'Pending ROI',
          none: 'No ROI',
        };
        return (
          <Badge
            variant="light"
            color={colorMap[status]}
            size="sm"
            style={{ cursor: status === 'none' ? undefined : 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              if (status !== 'none') openRoiView(c);
            }}
          >
            {labelMap[status]}
          </Badge>
        );
      },
      getValue: (c) => resolveRoiStatus(c.roi),
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      render: (c) => {
        const status = resolveRoiStatus(c.roi);
        return (
          <Group justify="flex-end" gap="xs">
            {status !== 'none' && (
              <Tooltip label="View ROI">
                <ActionIcon
                  variant="subtle"
                  color={status === 'active' ? 'teal' : 'orange'}
                  onClick={(e) => {
                    e.stopPropagation();
                    openRoiView(c);
                  }}
                  aria-label="View ROI for contact"
                >
                  <IconFileCertificate size={16} />
                </ActionIcon>
              </Tooltip>
            )}
            {c.source === 'related-person' && c.active !== false ? (
              <Tooltip label="Deactivate">
                <ActionIcon
                  variant="subtle"
                  color="orange"
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmKind('deactivate');
                    setConfirmContact(c);
                  }}
                  disabled={saving}
                  aria-label="Deactivate contact"
                >
                  <IconUserOff size={16} />
                </ActionIcon>
              </Tooltip>
            ) : null}
            {c.source === 'related-person' && c.active === false ? (
              <Tooltip label="Reactivate">
                <ActionIcon
                  variant="subtle"
                  color="teal"
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmKind('reactivate');
                    setConfirmContact(c);
                  }}
                  disabled={saving}
                  aria-label="Reactivate contact"
                >
                  <IconUserCheck size={16} />
                </ActionIcon>
              </Tooltip>
            ) : null}
            {c.source === 'patient-contact' ? (
              <Tooltip label="Remove">
                <ActionIcon
                  variant="subtle"
                  color="red"
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmKind('remove');
                    setConfirmContact(c);
                  }}
                  disabled={saving}
                  aria-label="Remove contact"
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Tooltip>
            ) : null}
          </Group>
        );
      },
      getValue: () => '',
    },
  ];

  if (loading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
        <Text size="sm" c="gray.6">Loading contacts…</Text>
      </Group>
    );
  }

  return (
    <Stack gap="lg">
      <Card withBorder>
        <Group justify="space-between" mb="md">
          <div>
            <Title order={4}>Contacts</Title>
            <Text size="sm" c="gray.6" mt="xs">
              Emergency contacts and related persons linked to this client. Add emergency contacts here; related persons are created through episodes of care and other workflows.
            </Text>
          </div>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={openAdd}
            color="gold"
            disabled={!patient}
          >
            Add Emergency Contact
          </Button>
        </Group>
        {contacts.length === 0 ? (
          <Text size="sm" c="gray.6" ta="center" py="lg">
            No contacts on file.
          </Text>
        ) : (
          <UnifyTable
            withPaper={false}
            columns={columns}
            data={contacts}
            getRowKey={(c) => c.id}
            onRowClick={openEdit}
          />
        )}
      </Card>

      <ClientContactFormModal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={isEdit ? 'edit' : 'create'}
        contact={isEdit ? editingContact : null}
        onSave={handleSave}
      />

      {/* ROI Add/View modal */}
      {roiContact && (
        <ContactRoiModal
          opened={roiModalOpen}
          onClose={closeRoi}
          contact={roiContact}
          patientId={patient?.id ?? ''}
          onSaved={async () => {
            closeRoi();
            await onRefresh();
          }}
        />
      )}

      <Modal
        opened={confirmKind !== null}
        onClose={closeConfirm}
        title={
          confirmKind === 'reactivate'
            ? 'Reactivate related person'
            : confirmKind === 'deactivate'
              ? 'Deactivate related person'
              : 'Remove contact'
        }
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            {confirmKind === 'reactivate' && confirmContact
              ? `Reactivate ${contactDisplayName(confirmContact)}? They will appear again wherever active related persons are shown (e.g. Medical Record contacts, Overview emergency list when applicable).`
              : confirmKind === 'deactivate' && confirmContact
                ? `This will deactivate ${contactDisplayName(confirmContact)}. They will no longer appear where only active related persons are listed. You can reactivate them here later.`
                : confirmContact
                  ? `Remove ${contactDisplayName(confirmContact)} from this client's emergency contacts? This cannot be undone.`
                  : ''}
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={closeConfirm}>Cancel</Button>
            <Button
              color={confirmKind === 'remove' ? 'red' : confirmKind === 'deactivate' ? 'orange' : 'teal'}
              onClick={handleConfirmedAction}
              loading={saving}
            >
              {confirmKind === 'reactivate' ? 'Reactivate' : confirmKind === 'deactivate' ? 'Deactivate' : 'Remove'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
